/*
 * Mask Zoo theme script (module, loaded once from snippets/mz-head.liquid).
 * Custom elements, so sections work again after the theme editor re-renders them:
 *   mz-squad          Build your crew: 4 slots, the mask grid, the live crew price, ONE /cart/add.js for the crew,
 *                     a crew bar on phones while the slots are out of view, the "Share your zoo" story picture
 *   mz-crew-pricing   product page crew block: "Add a second mask" row (one request for both), re-read on variant pick
 *   mz-rail           arrows for the scroll-snap rails (fine pointers)
 *   mz-join-steps     two-step sign-up on the customer form
 *   mz-copy           copy a code
 * Plus: collection filter panel toggle and submit-on-change.
 * Cart calls go through factory-kit (window.MZ_KIT = its asset url, the same module the blocks load), so Horizon's
 * drawer, bubble and fly-to-cart update as they do for the native product form.
 * Guards: html.mz-anim (motion setting on, no reduced motion, no html.factory-lowfx) for the fly and count-down;
 * only transform and opacity move; everything works without them.
 */

const root = document.documentElement;
const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionOn = () => root.classList.contains('mz-anim') && !reduceQuery.matches && !root.classList.contains('factory-lowfx');
const kit = () => (window.MZ_KIT ? import(window.MZ_KIT) : Promise.reject(new Error('factory-kit.js is missing')));

/** Money in the shopper's currency, built from a Liquid sample (123456 | money), so markets keep their format. */
function moneyFormatter(sample) {
  const text = String(sample || '');
  const found = text.match(/\d[\d.,'’\s ]*\d/);
  if (!found) return (cents) => (Math.round(cents) / 100).toFixed(2);
  const run = found[0];
  const before = text.slice(0, found.index);
  const after = text.slice(found.index + run.length);
  const tail = run.match(/(\D)(\d{2})$/);
  const decimal = run.replace(/\D/g, '') === '123456' && tail ? tail[1] : '';
  const whole = decimal ? run.slice(0, -3) : run;
  const group = (whole.match(/\D/) || [''])[0];
  const groups = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return (cents) => {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    if (!decimal) return before + groups(String(Math.round(value / 100))) + after;
    const minor = value % 100;
    const body = groups(String(Math.floor(value / 100))) + (minor ? decimal + String(minor).padStart(2, '0') : '');
    return before + body + after;
  };
}

const escapeHTML = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Fills [token] in a plain template (the data-* texts are escaped by Liquid and read back as plain text). */
function fill(template, values) {
  let out = String(template || '');
  for (const [key, value] of Object.entries(values)) out = out.split(`[${key}]`).join(String(value));
  return out;
}

/** The same with bold numbers, for innerHTML. */
function fillBold(template, values) {
  let out = escapeHTML(template || '');
  for (const [key, value] of Object.entries(values)) out = out.split(`[${key}]`).join(`<b class="mz-num">${escapeHTML(value)}</b>`);
  return out;
}

function storage() {
  try {
    const s = window.sessionStorage;
    const probe = '__mz';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch (e) {
    return null;
  }
}

/* ------------------------------------------------------------------ Build your crew */
const CREW_KEY = 'mz-crew-v1';

class MzSquad extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    const d = this.dataset;
    this.money = moneyFormatter(d.money);
    this.offs = String(d.offs || '').split(',').map((n) => Number(n) || 0);
    this.unit = Number(d.unit) || 0;
    this.slots = [null, null, null, null];
    this.picks = Array.from(this.querySelectorAll('[data-pick]'));
    this.picks.forEach((b) => b.dataset.labelJs && b.setAttribute('aria-label', b.dataset.labelJs));
    this.deck = this.querySelector('[data-deck]');
    this.bar = this.querySelector('[data-bar]');
    this.pickArea = this.querySelector('[data-pick-area]');
    this.totalEls = Array.from(this.querySelectorAll('[data-total]'));
    this.shown = 0;
    const eachOut = this.querySelector('[data-each-out]');
    this.startText = eachOut ? eachOut.textContent : '';
    const share = this.querySelector('[data-share]');
    this.canShare = Boolean(share) && !root.classList.contains('factory-inapp') && 'toBlob' in HTMLCanvasElement.prototype;

    this.onClick = (event) => this.click(event);
    this.addEventListener('click', this.onClick);
    this.restore();
    this.render(false);
    this.watchBar();
  }

  disconnectedCallback() {
    this._on = false;
    this.removeEventListener('click', this.onClick);
    this.io?.disconnect();
    cancelAnimationFrame(this.tween || 0);
  }

  click(event) {
    const pick = event.target.closest('[data-pick]');
    if (pick && this.contains(pick)) {
      event.preventDefault();
      this.pick(pick);
      return;
    }
    const slotBtn = event.target.closest('[data-slot-btn]');
    if (slotBtn && this.contains(slotBtn)) {
      const index = Number(slotBtn.closest('[data-slot]')?.dataset.slot);
      if (this.slots[index]) this.remove(index);
      else this.pickArea?.querySelector('[data-pick]')?.focus({ preventScroll: false });
      return;
    }
    const add = event.target.closest('[data-add]');
    if (add && this.contains(add)) {
      this.add();
      return;
    }
    const chip = event.target.closest('[data-chip]');
    if (chip && this.contains(chip)) {
      const list = this.querySelector('[data-picks]');
      if (list) {
        if (chip.dataset.chip) list.dataset.filter = chip.dataset.chip;
        else delete list.dataset.filter;
      }
      this.querySelectorAll('[data-chip]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
      return;
    }
    const share = event.target.closest('[data-share]');
    if (share && this.contains(share)) this.share(share);
  }

  /** What a grid button puts in a slot (the size select of that mask wins when there is one). */
  entryOf(button) {
    const select = button.closest('form')?.querySelector('[data-variant-select]');
    const option = select ? select.selectedOptions[0] : null;
    return {
      variant: option ? option.value : button.dataset.variant,
      price: Number(option ? option.dataset.price : button.dataset.price) || 0,
      title: button.dataset.title || '',
      img: button.dataset.img || '',
      imgLg: button.dataset.imgLg || button.dataset.img || '',
      tribe: button.dataset.tribe || '',
      key: button.dataset.variant,
    };
  }

  pick(button) {
    const index = this.slots.indexOf(null);
    if (index === -1) {
      this.say(this.dataset.full);
      if (motionOn()) this.deck?.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(0)' }], { duration: 260 });
      return;
    }
    this.say('');
    this.slots[index] = this.entryOf(button);
    const barOn = this.bar && this.bar.classList.contains('is-on');
    const target = (barOn ? this.bar : this.deck)?.querySelector(`[data-slot="${index}"]`);
    const img = button.querySelector('img');
    if (target && img && motionOn()) {
      target.classList.add('is-landing');
      this.fly(img, target.querySelector('[data-face]') || target).then(() => target.classList.remove('is-landing'));
    }
    this.render(true);
    this.save();
  }

  remove(index) {
    this.slots[index] = null;
    this.say('');
    this.render(true);
    this.save();
  }

  /** A copy of the mask photo arcs from the grid into the slot. transform / opacity only, 420 ms. */
  fly(fromImg, toEl) {
    const a = fromImg.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    if (!a.width || !b.width) return Promise.resolve();
    const ghost = document.createElement('img');
    ghost.src = fromImg.currentSrc || fromImg.src;
    ghost.alt = '';
    ghost.className = 'mz-ghost';
    Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px` });
    document.body.appendChild(ghost);
    const dx = b.left - a.left;
    const dy = b.top - a.top;
    const s = b.width / a.width;
    const anim = ghost.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.55 - 40}px) scale(${(1 + s) / 2})`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0.6 },
      ],
      { duration: 420, easing: 'cubic-bezier(0.3, 0.7, 0.3, 1)' }
    );
    const done = () => ghost.remove();
    const cancel = () => anim.finish();
    window.addEventListener('keydown', cancel, { once: true });
    return anim.finished.then(done, done).finally(() => window.removeEventListener('keydown', cancel));
  }

  totals() {
    const filled = this.slots.filter(Boolean);
    const n = filled.length;
    const sum = filled.reduce((t, s) => t + s.price, 0);
    const off = this.offs[n] || 0;
    const total = Math.max(0, sum - off);
    return { n, sum, off, total, each: n ? Math.round(total / n) : 0 };
  }

  render(animate) {
    const d = this.dataset;
    const { n, off, total, each } = this.totals();

    this.querySelectorAll('[data-slot]').forEach((li) => {
      const index = Number(li.dataset.slot);
      const entry = this.slots[index];
      const face = li.querySelector('[data-face]');
      const name = li.querySelector('[data-name]');
      const btn = li.querySelector('[data-slot-btn]');
      li.classList.toggle('is-filled', Boolean(entry));
      if (entry) {
        li.style.setProperty('--mz-c', entry.tribe ? `var(--mz-t${entry.tribe})` : 'var(--mz-soft-2)');
        li.style.setProperty('--mz-c-on', entry.tribe ? `var(--mz-t${entry.tribe}-on)` : 'var(--mz-ink)');
        if (face && face.dataset.key !== `${entry.variant}`) {
          face.dataset.key = `${entry.variant}`;
          face.innerHTML = entry.img ? `<img src="${escapeHTML(entry.img)}" alt="" width="160" height="160">` : '';
        }
        if (name) name.textContent = entry.title;
        btn?.setAttribute('aria-label', `${fill(d.labelSlot, { n: index + 1 })}: ${fill(d.labelRemove, { name: entry.title })}`);
      } else {
        li.style.removeProperty('--mz-c');
        li.style.removeProperty('--mz-c-on');
        if (face) {
          face.innerHTML = '';
          delete face.dataset.key;
        }
        if (name) name.textContent = '';
        btn?.setAttribute('aria-label', `${fill(d.labelSlot, { n: index + 1 })}, ${d.labelEmpty || ''}`);
      }
    });

    const count = this.querySelector('[data-count-out]');
    if (count) count.textContent = fill(d.count, { count: n });
    this.countTo(total, animate);

    const eachOut = this.querySelector('[data-each-out]');
    if (eachOut) eachOut.innerHTML = n ? fillBold(d.each, { each: this.money(each) }) : escapeHTML(this.startText);
    const saveOut = this.querySelector('[data-save-out]');
    if (saveOut) {
      const parts = [];
      if (off > 0) parts.push(fillBold(d.save, { amount: this.money(off) }));
      if (n >= 1 && n < 4) {
        const next = Math.round((this.unit * (n + 1) - (this.offs[n + 1] || 0)) / (n + 1));
        if (next < each) parts.push(fillBold(d.next, { each: this.money(next) }));
      } else if (n === 4 && d.top) parts.push(escapeHTML(d.top));
      saveOut.innerHTML = parts.join(' ');
      saveOut.hidden = parts.length === 0;
    }

    this.querySelectorAll('[data-step]').forEach((step) => {
      const k = Number(step.dataset.step);
      step.classList.toggle('is-on', k === n);
      step.classList.toggle('is-past', k < n);
    });

    const label = n === 0 ? d.buttonEmpty : fill(n === 1 ? d.buttonOne : d.buttonMany, { total: this.money(total) });
    this.querySelectorAll('[data-add]').forEach((b) => {
      if (!b.hasAttribute('aria-busy')) b.disabled = n === 0;
      b.textContent = label;
    });

    this.picks.forEach((b) => {
      const inCrew = this.slots.filter((s) => s && s.key === b.dataset.variant).length;
      const badge = b.querySelector('[data-in]');
      if (badge) {
        badge.textContent = inCrew ? String(inCrew) : '';
        badge.hidden = inCrew === 0;
      }
      b.closest('.mz-pick')?.classList.toggle('is-in', inCrew > 0);
    });

    const share = this.querySelector('[data-share]');
    if (share) share.hidden = !this.canShare || n === 0;
    this.classList.toggle('has-crew', n > 0);
  }

  /** The total counts to its new value in 360 ms (the price "drops on the spot"); still under reduced motion. */
  countTo(target, animate) {
    cancelAnimationFrame(this.tween || 0);
    const from = this.shown;
    const set = (v) => {
      this.shown = v;
      const text = this.money(v);
      this.totalEls.forEach((el) => (el.textContent = text));
    };
    if (!animate || !motionOn() || from === target) {
      set(target);
      return;
    }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 360);
      const eased = 1 - Math.pow(1 - t, 3);
      set(Math.round(from + (target - from) * eased));
      if (t < 1) this.tween = requestAnimationFrame(step);
      else set(target);
    };
    this.tween = requestAnimationFrame(step);
  }

  say(text) {
    const out = this.querySelector('[data-error]');
    if (!out) return;
    out.textContent = text || '';
    out.hidden = !text;
  }

  async add() {
    const filled = this.slots.filter(Boolean);
    if (!filled.length) return;
    const counts = new Map();
    filled.forEach((s) => counts.set(s.variant, (counts.get(s.variant) || 0) + 1));
    const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    const buttons = Array.from(this.querySelectorAll('[data-add]'));
    buttons.forEach((b) => {
      b.setAttribute('aria-busy', 'true');
      b.disabled = true;
    });
    let result = { ok: false };
    try {
      const { addToCart } = await kit();
      result = await addToCart(items, this, 'product');
    } catch (e) {
      result = { ok: false };
    }
    buttons.forEach((b) => b.removeAttribute('aria-busy'));
    if (result.ok) {
      this.slots = [null, null, null, null];
      this.save();
      this.render(true);
      this.say(this.dataset.added);
    } else {
      this.render(false);
      this.say(result.message || this.dataset.error);
    }
  }

  save() {
    const s = storage();
    if (!s) return;
    try {
      s.setItem(CREW_KEY, JSON.stringify(this.slots.map((e) => (e ? [e.key, e.variant] : null))));
    } catch (e) {
      /* storage full or blocked: the crew just isn't remembered */
    }
  }

  restore() {
    const s = storage();
    if (!s) return;
    let saved = null;
    try {
      saved = JSON.parse(s.getItem(CREW_KEY) || 'null');
    } catch (e) {
      saved = null;
    }
    if (!Array.isArray(saved)) return;
    saved.slice(0, 4).forEach((pair, i) => {
      if (!Array.isArray(pair)) return;
      const button = this.picks.find((b) => b.dataset.variant === String(pair[0]));
      if (!button) return;
      const select = button.closest('form')?.querySelector('[data-variant-select]');
      if (select && pair[1]) {
        const option = Array.from(select.options).find((o) => o.value === String(pair[1]) && !o.disabled);
        if (option) select.value = option.value;
      }
      this.slots[i] = this.entryOf(button);
    });
  }

  /** The crew bar on phones: shows while the grid is on screen and the slots are not. */
  watchBar() {
    if (!this.bar || !this.deck || !this.pickArea || !('IntersectionObserver' in window)) return;
    const state = { deck: true, pick: false };
    const apply = () => {
      const on = !state.deck && state.pick;
      this.bar.classList.toggle('is-on', on);
      this.bar.toggleAttribute('inert', !on);
      this.bar.setAttribute('aria-hidden', String(!on));
    };
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.deck) state.deck = entry.isIntersecting;
        if (entry.target === this.pickArea) state.pick = entry.isIntersecting;
      });
      apply();
    }, { threshold: 0 });
    this.io.observe(this.deck);
    this.io.observe(this.pickArea);
  }

  /** "Share your zoo": the picked faces on a 1080 x 1920 story picture, shared or saved. */
  async share(button) {
    const filled = this.slots.filter(Boolean);
    if (!filled.length || button.hasAttribute('aria-busy')) return;
    button.setAttribute('aria-busy', 'true');
    try {
      const blob = await this.drawStory();
      const file = new File([blob], 'mask-zoo-crew.png', { type: 'image/png' });
      let shared = false;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: this.dataset.shareTitle || '' });
          shared = true;
        } catch (e) {
          shared = Boolean(e && e.name === 'AbortError'); // the shopper closed the sheet: no download then
        }
      }
      if (!shared) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        this.say(this.dataset.shareDone);
      }
    } catch (e) {
      button.hidden = true;
    }
    button.removeAttribute('aria-busy');
  }

  async drawStory() {
    const W = 1080;
    const H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const css = getComputedStyle(root);
    const v = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
    const white = v('--mz-white', '#FFFFFF');
    const ink = v('--mz-ink', '#121318');
    const soft = v('--mz-soft-2', '#ECECEE');
    if (document.fonts && document.fonts.load) {
      await Promise.all([document.fonts.load('120px "Bagel Fat One"'), document.fonts.load('600 44px Sora')]).catch(() => {});
    }
    const load = (src) =>
      new Promise((resolve) => {
        if (!src) return resolve(null);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src.startsWith('//') ? `https:${src}` : src;
      });
    const images = await Promise.all(this.slots.map((s) => (s ? load(s.imgLg) : null)));
    const plate = (x, y, w, h, r, color) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
      else ctx.rect(x, y, w, h);
      ctx.fill();
    };

    ctx.fillStyle = white;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.font = '104px "Bagel Fat One", sans-serif';
    ctx.fillText(this.dataset.wordmark || '', W / 2, 230);
    ctx.font = '600 52px Sora, sans-serif';
    ctx.fillText(this.dataset.shareHeading || '', W / 2, 330);

    const size = 440;
    const gap = 40;
    const x0 = (W - size * 2 - gap) / 2;
    const y0 = 430;
    this.slots.forEach((entry, i) => {
      const x = x0 + (i % 2) * (size + gap);
      const y = y0 + Math.floor(i / 2) * (size + 150);
      const color = entry && entry.tribe ? v(`--mz-t${entry.tribe}`, soft) : soft;
      plate(x, y, size, size, 48, color);
      const img = images[i];
      if (img) {
        ctx.save();
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x + 24, y + 24, size - 48, size - 48, 32);
        else ctx.rect(x + 24, y + 24, size - 48, size - 48);
        ctx.clip();
        const scale = Math.max((size - 48) / img.width, (size - 48) / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, x + 24 + (size - 48 - w) / 2, y + 24 + (size - 48 - h) / 2, w, h);
        ctx.restore();
      }
      ctx.fillStyle = ink;
      ctx.font = entry ? '600 44px Sora, sans-serif' : '96px "Bagel Fat One", sans-serif';
      if (entry) ctx.fillText(entry.title.slice(0, 22), x + size / 2, y + size + 70);
      else ctx.fillText(String(i + 1), x + size / 2, y + size / 2 + 34);
    });

    ctx.font = '600 40px Sora, sans-serif';
    ctx.fillStyle = ink;
    ctx.fillText(this.dataset.shareSite || '', W / 2, H - 150);
    return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('no picture'))), 'image/png'));
  }
}
if (!customElements.get('mz-squad')) customElements.define('mz-squad', MzSquad);

/* ------------------------------------------------------------------ product page: crew pricing + second mask */
class MzCrewPricing extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.money = moneyFormatter(this.dataset.money);
    this.section = this.closest('.shopify-section, dialog');
    this.onClick = (event) => this.click(event);
    this.onSelect = (event) => this.refresh(event);
    this.addEventListener('click', this.onClick);
    this.section?.addEventListener('shopify:product:select', this.onSelect);
    this.enhance();
  }

  disconnectedCallback() {
    this._on = false;
    this.removeEventListener('click', this.onClick);
    this.section?.removeEventListener('shopify:product:select', this.onSelect);
  }

  enhance() {
    this.querySelectorAll('[data-pair-variant]').forEach((a) => a.setAttribute('role', 'button'));
  }

  price() {
    return Number(this.querySelector('[data-mz-price]')?.dataset.mzPrice) || 0;
  }

  currentVariant() {
    const scope = this.section || document;
    const input = scope.querySelector('product-form-component form[data-type="add-to-cart-form"] input[name="id"]');
    return input?.value || this.querySelector('[data-mz-price]')?.dataset.variant || '';
  }

  click(event) {
    const item = event.target.closest('[data-pair-variant]');
    if (item && this.contains(item)) {
      event.preventDefault();
      const on = item.getAttribute('aria-pressed') !== 'true';
      this.querySelectorAll('[data-pair-variant]').forEach((a) => a.setAttribute('aria-pressed', String(a === item && on)));
      this.picked = on ? item : null;
      this.update();
      return;
    }
    const add = event.target.closest('[data-pair-add]');
    if (add && this.contains(add)) this.add(add);
  }

  update() {
    const button = this.querySelector('[data-pair-add]');
    if (!button) return;
    if (!this.picked) {
      button.disabled = true;
      button.textContent = button.dataset.empty || button.textContent;
      return;
    }
    if (!button.dataset.empty) button.dataset.empty = button.textContent;
    const pair = Number(this.picked.dataset.price) || 0;
    const sum = this.price() + pair;
    const fixed = Number(this.dataset.offFixed) || 0;
    const pct = Number(this.dataset.offPct) || 0;
    const off = fixed || Math.round((sum * pct) / 100);
    button.disabled = false;
    button.textContent = fill(this.dataset.button, { total: this.money(sum - off) });
  }

  async add(button) {
    const current = this.currentVariant();
    if (!this.picked || !current || button.hasAttribute('aria-busy')) return;
    const error = this.querySelector('[data-pair-error]');
    button.setAttribute('aria-busy', 'true');
    let result = { ok: false };
    try {
      const { addToCart } = await kit();
      const items = [{ id: Number(current), quantity: 1 }, { id: Number(this.picked.dataset.pairVariant), quantity: 1 }];
      result = await addToCart(items, this, 'product');
    } catch (e) {
      result = { ok: false };
    }
    button.removeAttribute('aria-busy');
    if (error) {
      error.textContent = result.ok ? '' : result.message || this.dataset.error || '';
      error.hidden = result.ok;
    }
  }

  /** Horizon fetches the section on every variant pick: take this block's fresh markup from it. */
  refresh(event) {
    if (!event.promise) return;
    event.promise
      .then(({ detail }) => {
        const html = detail && detail.html;
        if (!html || !html.querySelector) return;
        const fresh = html.querySelector(`[data-mz-refresh="${this.dataset.mzRefresh}"]`);
        if (!fresh) return;
        const open = this.querySelector('details[open]') !== null;
        this.innerHTML = fresh.innerHTML;
        if (open) this.querySelector('details')?.setAttribute('open', '');
        this.picked = null;
        this.enhance();
      })
      .catch(() => {});
  }
}
if (!customElements.get('mz-crew-pricing')) customElements.define('mz-crew-pricing', MzCrewPricing);

/* ------------------------------------------------------------------ rails: arrows */
class MzRail extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rail-list]');
    this.prev = this.querySelector('[data-dir="-1"]');
    this.next = this.querySelector('[data-dir="1"]');
    if (!this.list || !this.prev || !this.next || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.onArrow = (event) => {
      const dir = Number(event.currentTarget.dataset.dir);
      const behavior = motionOn() ? 'smooth' : 'auto';
      this.list.scrollBy({ left: dir * this.list.clientWidth * 0.85, behavior });
    };
    this.onScroll = () => this.sync();
    this.prev.addEventListener('click', this.onArrow);
    this.next.addEventListener('click', this.onArrow);
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.sync();
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  sync() {
    const max = this.list.scrollWidth - this.list.clientWidth;
    const scrollable = max > 4;
    this.prev.hidden = !scrollable;
    this.next.hidden = !scrollable;
    this.prev.disabled = this.list.scrollLeft <= 4;
    this.next.disabled = this.list.scrollLeft >= max - 4;
  }
}
if (!customElements.get('mz-rail')) customElements.define('mz-rail', MzRail);

/* ------------------------------------------------------------------ two-step sign-up */
class MzJoinSteps extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.form = this.closest('form');
    this.mail = this.querySelector('input[type="email"]');
    this.tags = this.querySelector('[data-join-tags]');
    this.error = this.querySelector('[data-join-error]');
    this.picked = new Set();
    this.querySelector('[data-join-next]')?.addEventListener('click', () => this.next());
    this.mail?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.next();
      }
    });
    this.querySelectorAll('[data-join-tag]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        if (on) this.picked.add(chip.dataset.joinTag);
        else this.picked.delete(chip.dataset.joinTag);
        this.writeTags();
      })
    );
    this.querySelector('[data-join-skip]')?.addEventListener('click', () => {
      this.picked.clear();
      this.writeTags();
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.step(2).classList.contains('is-on')) {
        event.preventDefault();
        this.next();
      }
    });
  }

  step(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  valid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((this.mail?.value || '').trim());
  }

  next() {
    if (!this.valid()) {
      if (this.error) this.error.hidden = false;
      this.mail?.setAttribute('aria-invalid', 'true');
      this.mail?.focus();
      return;
    }
    if (this.error) this.error.hidden = true;
    this.mail?.removeAttribute('aria-invalid');
    this.step(1).classList.remove('is-on');
    this.step(2).classList.add('is-on');
    this.step(2).querySelector('button')?.focus({ preventScroll: true });
  }

  writeTags() {
    if (!this.tags) return;
    this.tags.value = [this.tags.dataset.base, ...this.picked].filter(Boolean).join(', ');
  }
}
if (!customElements.get('mz-join-steps')) customElements.define('mz-join-steps', MzJoinSteps);

/* ------------------------------------------------------------------ copy a code */
class MzCopy extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button || this._on) return;
    this._on = true;
    button.addEventListener('click', () => {
      const text = this.dataset.copy || '';
      const was = button.textContent;
      const done = () => {
        button.textContent = this.dataset.done || was;
        setTimeout(() => (button.textContent = was), 1400);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('mz-copy')) customElements.define('mz-copy', MzCopy);

/* ------------------------------------------------------------------ collection: filters panel + submit on change */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest && event.target.closest('[data-filters-toggle]');
  if (!toggle) return;
  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;
  const open = toggle.getAttribute('aria-expanded') !== 'true';
  toggle.setAttribute('aria-expanded', String(open));
  panel.classList.toggle('is-open', open);
});

document.addEventListener('change', (event) => {
  const form = event.target.closest && event.target.closest('form[data-mz-autosubmit]');
  if (!form) return;
  if (event.target.type === 'number') return; // prices submit with the button or Enter
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.submit();
});

/* empty price boxes stay out of the filter url */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-mz-autosubmit]')) return;
  form.querySelectorAll('input[type="number"]').forEach((input) => {
    if (input.value === '') input.disabled = true;
  });
});

/* the join form reloads the page after posting: bring the code into view */
if (window.location.search.includes('customer_posted=true')) {
  requestAnimationFrame(() => document.querySelector('.mz-join__done')?.focus({ preventScroll: false }));
}
